import { EditorProps } from "@grapesjs/react";
import grapesjs, { Block, Editor, EditorConfig } from "grapesjs";
import { useEffect, useRef, useState } from "react";

import ReactDOMServer from "react-dom/server";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import swal from "sweetalert";
import CustomDesignedEditor from "../../components/GrapesJS/CustomDesignedEditor";
import ImagePopUpV3 from "../../components/ImagePopUpV3";
import { Page } from "../../features/addPages/addPages";
import { setGlobalPopupElement, setLoading } from "../../features/popupSlice";
import {
    fetchImagesModule,
    getPageDetails,
    updateAllPages,
} from "../../modules/mediaManager";
import { CustomComponentSettingsFunction } from "../../settings";
import {
    addCustomComponents,
    addProductComponents,
} from "../../settings/customWidgetComponents";
import { getImageUrl } from "../../utils";
import { customPageWidgetName, customWidgets } from "../../utils/customWidgets";
import { showToast } from "../HomePage";
import landingPages from "../../utils/landingPages";

declare global {
    interface Window {
        grapesjs: typeof grapesjs;
    }
}

export interface ExtraEditorProps {
    page?: Page;
    publishPage?: () => void;
    exportJson?: () => void;
    saveThemeGlobal?: () => void;
    addProducts?: () => void;
    addBlockToRoot?: (block: Block) => void;
    updateThemeInfo?: () => void;
    filteredCategories?: string[];
}

export const defaultOptions: EditorConfig = {
    storageManager: false,
    height: "100vh",
    assetManager: {
        // assets: [],
        // custom: true,
        upload: false,
        autoAdd: false,
    },
    undoManager: {
        trackSelection: false,
    },
    selectorManager: {
        componentFirst: true,
    },
    components: landingPages.landingPage1.content,
};

const initialData = {
    components: [],
    // style: [
    // 	{
    // 		selectors: ["#ih4y"],
    // 		style: {
    // 			padding: "10px",
    // 		},
    // 	},
    // ],
};

// Simulate an asynchronous API call with pagination
const fetchAssets = async (page: number, limit = 10): Promise<any[]> => {
    return new Promise(async (resolve, reject) => {
        try {
            const res = await fetchImagesModule({
                currentPage: page,
                lastPage: 10,
                total: 100,
            });

            resolve(res?.data || []);
        } catch (error) {
            reject(error);
        }
    });
};

export const plugins: EditorProps["plugins"] = [
    {
        id: "gjs-blocks-basic",
        src: "https://unpkg.com/grapesjs-blocks-basic",
    },
    "grapesjs-plugin-forms",
    "grapesjs-component-countdown",
];

const widgetName = customPageWidgetName;

const CustomPage = () => {
    const dispatch = useDispatch();
    const { pageTitle: pageId } = useParams();

    const [page, setPage] = useState<Page | null>(null);
    const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
    const [editorSettings, setEditorSettings] = useState<{
        [key: string]: any;
    }>({});

    const [currentPage, setCurrentPage] = useState(0);
    const [isFetching, setIsFetching] = useState(false);
    const [assets, setAssets] = useState<any[]>([]);
    // const [selectedComponent, setSelectedComponent] = useState<any>(null);
    const selectedComponent = useRef<any>(null);

    useEffect(() => {
        if (pageId && editorInstance) {
            dispatch(setLoading(true));

            getPageDetails(pageId)
                .then((res) => {
                    setPage(res.data);

                    const widget = res.data?.widgets?.find(
                        (widget: any) => widget.name === widgetName
                    );

                    if (widget && editorInstance) {
                        const data = widget.data;

                        editorInstance.setComponents(data.components);
                        editorInstance.setStyle(data.style);
                        console.log("data.style :>> ", data.style);
                    }
                })
                .finally(() => {
                    dispatch(setLoading(false));
                });
        }
    }, [pageId, dispatch, editorInstance]);

    const onEditor = (editor: Editor) => {
        console.log("Editor loaded", { editor });

        // editor.on("component:add component:remove component:update", () => {
        // 	const htmlOutput = editor.getHtml();
        // 	const cssOutput = editor.getCss();
        // 	const jsonOutput = editor.getComponents().toJSON();

        // 	console.log("HTML:", htmlOutput);
        // 	console.log("CSS:", cssOutput);
        // 	console.log("Components as JSON:", jsonOutput);
        // });

        const addCustomWidget = (
            editor: Editor,
            data: {
                className: string;
                content: string;
                label: string;
                media: string;
                category: string;
                customSettings?: CustomComponentSettingsFunction;
            }
        ) => {
            data.customSettings?.(
                editor,
                data.className,
                data.content,
                {
                    data: editorSettings,
                    setData: setEditorSettings,
                },
                {
                    dispatch: dispatch,
                }
            );

            // const cssComposer = editor.CssComposer;
            // const sm = editor.SelectorManager;
            // var c1 = sm.add(data.className);

            // const rule = cssComposer.add([c1]);

            // rule.set("style", {});

            editor.BlockManager.add(data.className, {
                label: data.label,
                media: data.media,
                // content: data.content,
                content: {
                    type: data.className, // Directly specify the custom component type
                    components: {
                        type: "html",
                        content: data.content,
                    },
                },
                category: data.category,
                // attributes: {
                // 	class: data.className,
                // 	title: data.label,
                // 	input: "text",
                // },
            });
        };

        customWidgets.forEach((widget) => {
            addCustomWidget(editor, {
                category: "Custom Widgets",
                className: widget.key,
                content: ReactDOMServer.renderToString(
                    <div className={widget.key} data-gjs-type={widget.key}>
                        <widget.comp />
                    </div>
                ),
                label: widget.name,
                media: ReactDOMServer.renderToString(<widget.comp />),
                customSettings: widget.customComponentSettings,
            });
        });

        // Add custom SVG component
        editor.DomComponents.addType("svg-component", {
            model: {
                defaults: {
                    tagName: "svg",
                    droppable: false,
                    draggable: true,
                    attributes: { class: "svg-container" },
                    components: `
				  <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
					<circle cx="50" cy="50" r="40" stroke="black" stroke-width="3" fill="red" />
				  </svg>
				`,
                    traits: [
                        {
                            type: "text",
                            label: "Circle Color",
                            name: "fill",
                            changeProp: true,
                        },
                    ],
                },

                init() {
                    this.on("change:fill", this.updateSVG);
                },

                updateSVG() {
                    const color = this.get("fill");
                    const svg = this.view?.el.querySelector("svg circle");
                    if (svg) {
                        svg.setAttribute("fill", color);
                    }
                },
            },

            view: {
                onRender() {
                    const svg = this.el.querySelector("svg");
                    if (svg) {
                        svg.setAttribute("width", "100%");
                    }
                },
            },
        });

        // Add to Block Manager
        editor.BlockManager.add("svg-block", {
            label: "SVG Component",
            content: { type: "svg-component" },
            category: "Custom Components",
        });

        // editor.setComponents(initialData.components);
        // editor.setStyle(initialData.style);

        // Override the default asset manager command
        editor.Commands.add("open-assets", {
            run(editor, sender) {
                console.log("editor open-assets :>> ", editor);
                dispatch(
                    setGlobalPopupElement(
                        <ImagePopUpV3
                            onClose={() =>
                                dispatch(setGlobalPopupElement(null))
                            }
                            onUpdate={(images) => {
                                console.log(
                                    "images :>> ",
                                    images,
                                    selectedComponent
                                );
                                if (selectedComponent) {
                                    // Update the 'src' attribute of the selected image component
                                    // selectedComponent.set("attributes", {
                                    // 	...selectedComponent.get("attributes"),
                                    // 	src: images?.[0],
                                    // });

                                    // Update the 'src' attribute of the selected image component
                                    selectedComponent.current?.set(
                                        "attributes",
                                        {
                                            ...selectedComponent.current.get(
                                                "attributes"
                                            ),
                                            src: getImageUrl(
                                                images?.[0]?.path || ""
                                            ),
                                        }
                                    );

                                    console.log(
                                        "selectedComponent :>> ",
                                        selectedComponent
                                    );
                                }
                                dispatch(setGlobalPopupElement(null));
                            }}
                        />
                    )
                );
                // setShowImagePopup(true); // Show the custom ImagePopup
                try {
                    sender?.set("active", false); // Turn off the default asset manager behavior
                } catch {}
            },
        });

        editor.on("component:selected", (component) => {
            if (component) {
                const tm = editor.TraitManager;
                console.log("tm :>> ", tm);
                if (tm) {
                    tm.getTraitsViewer()?.trigger("open");
                } else {
                    console.warn("Traits Manager is not available.");
                }
            }
        });

        editor.DomComponents.addType("accordion", {
            model: {
                defaults: {
                    tagName: "div",
                    classes: ["accordion"],
                    droppable: false,
                    traits: [
                        {
                            type: "text",
                            label: "Panel 1 Title",
                            name: "panel1Title",
                            changeProp: true,
                        },
                        {
                            type: "text",
                            label: "Panel 1 Content",
                            name: "panel1Content",
                            changeProp: true,
                        },
                    ],
                    components: `
				  <div>
					<button class="accordion-button">Panel 1</button>
					<div class="accordion-content">
					  <p>This is panel 1 content.</p>
					</div>
				  </div>
				`,
                },
                init() {
                    this.on("change:panel1Title", this.updatePanel1Title);
                    this.on("change:panel1Content", this.updatePanel1Content);
                },
                updatePanel1Title() {
                    const btn =
                        this.view?.el.querySelector(".accordion-button");
                    if (btn) btn.textContent = this.get("panel1Title");
                },
                updatePanel1Content() {
                    const content = this.view?.el.querySelector(
                        ".accordion-content p"
                    );
                    if (content)
                        content.textContent = this.get("panel1Content");
                },
            },
            view: {
                onRender() {
                    const buttons =
                        this.el.querySelectorAll(".accordion-button");
                    buttons.forEach((button) => {
                        button.addEventListener("click", () => {
                            const content = button.nextElementSibling as any;
                            if (content?.style?.display === "block") {
                                content!.style!.display = "none";
                            } else {
                                content!.style!.display = "block";
                            }
                        });
                    });
                },
            },
        });

        editor.BlockManager.add("accordion", {
            label: "Accordion",
            category: "Custom Components",
            content: { type: "accordion" },
        });

        editor.addStyle(`
			.accordion-button {
			  display: block;
			  width: 100%;
			  text-align: left;
			  padding: 10px;
			  font-size: 16px;
			  cursor: pointer;
			  border: 1px solid #ddd;
			  background: #f9f9f9;
			}
		  
			.accordion-content {
			  display: none;
			  padding: 10px;
			  border: 1px solid #ddd;
			  border-top: none;
			}
		  
			.bullet-list {
			  list-style-type: disc;
			  padding-left: 20px;
			}
		  `);

        editor.BlockManager.add("accordion", {
            label: "Accordion",
            category: "Custom Components",
            content: { type: "accordion" },
        });

        // Listen for component selection events
        editor.on("component:selected", (model) => {
            const type = model.get("type");
            console.log("model, type :>> ", model, type);
            if (type === "image") {
                // setSelectedComponent(model); // Set the selected image component
                selectedComponent.current = model;
            } else {
                // setSelectedComponent(null);
                selectedComponent.current = null;
            }
        });

        // editor.BlockManager.add("product-card", {
        // 	label: "Product Card",
        // 	category: "Custom Widgets",
        // 	attributes: { class: "fa fa-id-card" },
        // 	content: `
        //     <div class="product-card" style="max-width:200px; text-align:center">
        //             <img src="https://via.placeholder.com/150" alt="Product Image" style="width: 200px; height:200px; border-radius: 4px;" data-gjs-editable="false" data-gjs-droppable="false" data-gjs-draggable="false">
        //             <h3 style="font-size: 18px; color: #333; margin: 10px 0;" data-gjs-editable="false" data-gjs-droppable="false" data-gjs-draggable="false">Product Title</h3>
        //             <p style="font-size: 14px; color: #555; margin: 0 0 10px;" data-gjs-editable="false" data-gjs-droppable="false" data-gjs-draggable="false">$19.99</p>
        //         </div>`,
        // });

        addProductComponents(editor, dispatch);

        addCustomComponents(editor);

        // Override the asset manager's load method to load assets asynchronously with pagination
        // editor.AssetManager.load = (async (params: any, clb: any) => {
        // 	console.log("assetmanage.load", params, clb);
        // 	// Fetch initial assets
        // 	await loadMoreAssets(clb, 0);
        // }) as any;

        // // Set up scroll event listener for infinite pagination
        // editor.on("asset:open", () => {
        // 	console.log("asset:open");
        // 	const assetContainer = editor.AssetManager.getContainer();
        // 	editor.AssetManager.add(
        // 		"http://placehold.it/350x250/78c5d6/fff/image1.jpg"
        // 	);
        // 	loadMoreAssets(editor.AssetManager.add, 1);

        // 	assetContainer.addEventListener("scroll", async () => {
        // 		// Check if user scrolled to the bottom and load more assets if needed
        // 		if (
        // 			assetContainer.scrollHeight - assetContainer.scrollTop <=
        // 				assetContainer.clientHeight + 50 &&
        // 			!isFetching
        // 		) {
        // 			setIsFetching(true);
        // 			loadMoreAssets(editor.AssetManager.add);
        // 		}
        // 	});
        // });

        setEditorInstance(editor); // Save the editor instance
    };

    // Function to load more assets for the asset manager
    const loadMoreAssets = async (callback: any, page = currentPage + 1) => {
        try {
            // Fetch the next page of assets asynchronously
            const res = await fetchAssets(page);
            const newAssets = res
                ?.filter((img) => !!img?.path)
                ?.map((image) => getImageUrl(image.path));
            console.log("newAssets :>> ", newAssets);

            // Add the new assets to the existing assets array
            setAssets((prevAssets) => [...prevAssets, ...newAssets]);

            // Trigger the callback to update GrapesJS asset manager
            callback(newAssets);

            // Update the current page and reset fetching state
            setCurrentPage(page);
            setIsFetching(false);
        } catch (error) {
            console.error(error);
        }
    };

    // Function to export JSON
    const exportJson = () => {
        if (editorInstance) {
            const components = editorInstance.getComponents();
            const style = editorInstance.getStyle();

            const exportComponentData = (component: any) => {
                const componentData = component.toJSON(); // Get the default JSON data of the component

                // Include the tagName in the exported data
                componentData.tagName = component.get("tagName");

                // Recursively process child components
                if (component.components) {
                    componentData.components = component
                        .components()
                        .map((child: any) => exportComponentData(child));
                }

                return componentData;
            };

            // Process each top-level component and recursively add tagName
            const exportedComponents = components.map((component) =>
                exportComponentData(component)
            );

            const exportedData = {
                components: exportedComponents,
                style: style,
            };

            // Convert the JSON to a readable string
            const jsonString = JSON.stringify(exportedData, null, 2);
            console.log("Exported JSON:", jsonString);

            // Optionally download the JSON as a file
            const blob = new Blob([jsonString], { type: "application/json" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = "grapesjs-data.json";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const patchPageData = (status: "draft" | "published", data: any) => {
        const widgets = [
            {
                name: widgetName,
                title: widgetName,
                active: true,
                data: data,
            },
        ];

        const handleUpdatePage = (screenMode?: string) => {
            const newData: any = {
                ...page,
                status: status,
                widgets: widgets,
                // settings: pageSettings,
                ...(screenMode && { screenMode: screenMode }),
            };

            updateAllPages(newData, widgets, "widgets")
                .then((res) => {
                    // dispatch(fetchAllPages())
                    setPage(res.data);
                    if (status === "published") {
                        swal("Good job!", "Page published", "success");
                    } else {
                        showToast("Added and saved as draft");
                    }
                })
                .catch(() => {
                    swal("Something went wrong!", "", "error");
                });
        };

        if (page?.is_front_page && status === "published") {
            handleUpdatePage("all");
        } else {
            handleUpdatePage();
        }
    };

    const publishPage = () => {
        if (editorInstance) {
            const components = editorInstance.getComponents();
            const style = editorInstance.getCss();

            const exportComponentData = (component: any) => {
                const componentData = component.toJSON(); // Get the default JSON data of the component

                // Include the tagName in the exported data
                componentData.tagName = component.get("tagName");

                // Recursively process child components
                if (component.components) {
                    componentData.components = component
                        .components()
                        .map((child: any) => exportComponentData(child));
                }

                return componentData;
            };

            // Process each top-level component and recursively add tagName
            const exportedComponents = components.map((component) =>
                exportComponentData(component)
            );

            const exportedData = {
                components: exportedComponents,
                style: style,
            };

            // Convert the JSON to a readable string
            const jsonString = JSON.stringify(exportedData, null, 2);
            console.log("Exported JSON:", jsonString, components, style);

            if (page) {
                patchPageData("published", exportedData);
            }
        }
    };

    const defaultEditorProps: EditorProps = {
        // grapesjs: window.grapesjs,
        grapesjs: grapesjs,
        // grapesjsCss: 'http://localhost:8080/dist/css/grapes.min.css',
        grapesjsCss: "https://unpkg.com/grapesjs/dist/css/grapes.min.css",
        plugins: plugins,
        options: defaultOptions,
        onChange: (props) => {
            console.log("Changes", props);
        },
        onUpdate: (props) => {
            console.log("Update", props);
        },
    };

    return (
        <CustomDesignedEditor
            {...defaultEditorProps}
            onEditor={onEditor}
            publishPage={publishPage}
            exportJson={exportJson}
        />
    );

    // return (
    // 	<div>
    // 		<button className="btn !w-auto mb-2 px-3 mr-3" onClick={exportJson}>
    // 			Export as JSON
    // 		</button>
    // 		<button className="btn !w-auto mb-2 px-3" onClick={publishPage}>
    // 			Publish
    // 		</button>
    // 		{/* <DefaultEditor {...defaultEditorProps} onEditor={onEditor} /> */}

    // 		<CustomDesignedEditor {...defaultEditorProps} onEditor={onEditor} />
    // 	</div>
    // );
};

export default CustomPage;
